import {
  CTable,
  CTableBody,
  CTableDataCell,
  CTableHead,
  CTableHeaderCell,
  CTableRow,
} from "@coreui/react";
import { faPercent } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { format } from "date-fns";
import React from "react";
import { Link } from "react-router-dom";
import { getBadgeClassNameByStatus } from "../../../helpers/colors";
import { RideStatus, RideStatusLabels } from "../../../helpers/enums";
import {
  formatDateString,
  paymentMethodHelper,
} from "../../../helpers/general";
import { ProfilePictureAvatar } from "../../taxi/Profile_picture_avatar";

interface Props {
  rides: Ride[];
}

const formatDistance = (meters: number) =>
  meters < 1000
    ? `${Math.round(meters)} m`
    : `${(meters / 1000).toFixed(1)} km`;

/* ─────────────── Small building blocks ─────────────── */

const thStyle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: "0.04em",
  textTransform: "uppercase",
  color: "#777",
  whiteSpace: "nowrap",
  padding: "12px 14px",
  backgroundColor: "#fafafa",
  borderBottom: "1px solid #eee",
};

const tdStyle: React.CSSProperties = {
  padding: "14px",
  verticalAlign: "middle",
  fontSize: 13,
};

function Chip({
  children,
  tone = "neutral",
}: {
  children: React.ReactNode;
  tone?: "neutral" | "success" | "danger" | "warning";
}) {
  const tones = {
    neutral: { fg: "#555", bg: "#f2f2f2" },
    success: { fg: "#198754", bg: "rgba(25,135,84,0.12)" },
    danger: { fg: "#dc3545", bg: "rgba(220,53,69,0.12)" },
    warning: { fg: "#f0813f", bg: "rgba(240,129,63,0.14)" },
  }[tone];

  return (
    <span
      className="d-inline-flex align-items-center gap-1 rounded-pill"
      style={{
        height: 22,
        padding: "0 9px",
        fontSize: 11,
        fontWeight: 700,
        whiteSpace: "nowrap",
        color: tones.fg,
        backgroundColor: tones.bg,
      }}
    >
      {children}
    </span>
  );
}

function DriverCell({ driver }: { driver?: any }) {
  if (!driver) return <span className="text-muted">—</span>;
  return (
    <div className="d-flex align-items-center gap-2" style={{ minWidth: 150 }}>
      <ProfilePictureAvatar
        profilePicture={driver.profilePicture}
        fullName={driver.fullName}
        isOnline={driver.driverProfile?.isOnline}
      />
      <Link
        to={`/drivers/${driver.id}`}
        className="fw-semibold text-capitalize"
      >
        {driver.fullName || "Inconnu"}
      </Link>
    </div>
  );
}

function RouteCell({
  from,
  to,
  distance,
  duration,
}: {
  from: string;
  to: string;
  distance?: number;
  duration?: number;
}) {
  const dot = (color: string): React.CSSProperties => ({
    width: 9,
    height: 9,
    borderRadius: "50%",
    backgroundColor: color,
    flexShrink: 0,
  });

  return (
    <div style={{ minWidth: 260, maxWidth: 340 }}>
      <div className="d-flex gap-2">
        <div
          className="d-flex flex-column align-items-center"
          style={{ width: 10, paddingTop: 5, paddingBottom: 5 }}
        >
          <span style={dot("#f0813f")} />
          <span
            style={{
              flex: 1,
              width: 2,
              margin: "3px 0",
              backgroundColor: "#e2e2e2",
            }}
          />
          <span style={dot("#22AAFF")} />
        </div>
        <div className="d-flex flex-column gap-2" style={{ minWidth: 0 }}>
          <span
            className="text-truncate"
            title={from}
            style={{ maxWidth: 310 }}
          >
            {from}
          </span>
          <span className="text-truncate" title={to} style={{ maxWidth: 310 }}>
            {to}
          </span>
        </div>
      </div>
      <div className="d-flex gap-1 mt-2 ms-3 ps-1">
        {distance != null && <Chip>{formatDistance(distance)}</Chip>}
        {duration ? <Chip>{Math.floor(duration / 60)} min</Chip> : null}
      </div>
    </div>
  );
}

/* ─────────────── Row ─────────────── */

function RideRow({ ride }: { ride: Ride }) {
  const coupon = ride?.priceCalculation?.coupon;
  const isCouponApplied = Boolean(coupon?.isCouponApplied);
  const discountAmount = isCouponApplied
    ? +ride?.originalFare - coupon?.fareAfterCoupon
    : 0;

  return (
    <CTableRow>
      {/* Ride: reference + date */}
      <CTableDataCell style={tdStyle}>
        <Link
          to={`/rides/${ride.id}/ridedetails`}
          state={{ ride }}
          className="fw-bold"
        >
          #{ride.reference || "N/A"}
        </Link>
        <div className="text-muted mt-1" style={{ fontSize: 12 }}>
          {formatDateString(ride.createdAt)} ·{" "}
          {format(new Date(ride.createdAt), "HH:mm")}
        </div>
        {isCouponApplied && (
          <div className="mt-1">
            <Chip tone="warning">
              -{coupon?.couponPercentage}
              <FontAwesomeIcon icon={faPercent} size="xs" />
              coupon
            </Chip>
          </div>
        )}
      </CTableDataCell>

      {/* Driver */}
      <CTableDataCell style={tdStyle}>
        <DriverCell driver={ride.driver} />
      </CTableDataCell>

      {/* Route */}
      <CTableDataCell style={tdStyle}>
        <RouteCell
          from={ride.pickupLocationName}
          to={ride.destinationLocationName}
          distance={ride.distance}
          duration={ride.estimatedDuration}
        />
      </CTableDataCell>

      {/* Status */}
      <CTableDataCell style={tdStyle}>
        <span
          className={
            ride.status === RideStatus.WAITING_PICKUP
              ? "badge-primary"
              : getBadgeClassNameByStatus(ride.status)
          }
        >
          {RideStatusLabels[ride.status as keyof typeof RideStatusLabels] ||
            "Statut inconnu"}
        </span>
      </CTableDataCell>

      {/* Payment */}
      <CTableDataCell style={tdStyle}>
        <div style={{ minWidth: 120 }}>
          <div className="d-flex align-items-baseline gap-2">
            <span className="fw-bold" style={{ fontSize: 15 }}>
              {ride.fare}
              <span
                className="text-muted fw-normal ms-1"
                style={{ fontSize: 11 }}
              >
                MAD
              </span>
            </span>
            {isCouponApplied && (
              <span
                className="text-muted"
                style={{ fontSize: 12, textDecoration: "line-through" }}
              >
                {ride.originalFare}
              </span>
            )}
          </div>
          {isCouponApplied && (
            <div className="text-success" style={{ fontSize: 12 }}>
              −{discountAmount} MAD
            </div>
          )}
          <div className="text-muted mt-1" style={{ fontSize: 12 }}>
            {paymentMethodHelper(ride?.paymentMethod)}
          </div>
        </div>
      </CTableDataCell>
    </CTableRow>
  );
}

/* ─────────────── Table ─────────────── */

export default function SimpleUserRidersTable({ rides }: Props) {
  return (
    <div className="mt-3">
      <CTable responsive hover align="middle" className="mb-0">
        <CTableHead>
          <CTableRow>
            <CTableHeaderCell scope="col" style={thStyle}>
              Trajet
            </CTableHeaderCell>
            <CTableHeaderCell scope="col" style={thStyle}>
              Chauffeur
            </CTableHeaderCell>
            <CTableHeaderCell scope="col" style={thStyle}>
              Itinéraire
            </CTableHeaderCell>
            <CTableHeaderCell scope="col" style={thStyle}>
              Statut
            </CTableHeaderCell>
            <CTableHeaderCell scope="col" style={thStyle}>
              Paiement
            </CTableHeaderCell>
          </CTableRow>
        </CTableHead>
        <CTableBody>
          {rides.map((ride) => (
            <RideRow key={ride.id} ride={ride} />
          ))}
        </CTableBody>
      </CTable>
    </div>
  );
}
